<template>
  <!--
    Used to edit the theme-elements that are used in the videoPostprocessing.
  -->
  <Portlet
    :title="portletTilte"
    icon="tv"
    class="themeElementEdit"
  >
    <template
      slot="buttons"
    >
      <toggle-button
        v-model="previewOtherElements"
        :labels="{ checked: 'show other', unchecked: 'hide other' }"
        class="ml-3 mt-2"
        :width="120"
        :height="30"
        :font-size="12"
      />
    </template>
    <LoadingPlaceholder v-if="loading" />
    <template v-else>
      <template v-if="layoutImageElement && themeId && elementId">
        <div
          class="el-table_1_column_1 is-left mb-3"
          colspan="2"
        >
          <div>
            <div class="cell row">
              <div class="col-sm-12 col-lg-8">
                <Preview
                  :element="layoutImageElement"
                  :edit="true"
                  :preview-other-elements="previewOtherElements"
                  :theme-id="themeId"
                  :parent-errors="$validator.errors.items"
                  :image-url="imageUrl"
                  tag="rect"
                  @changeAttribute="changeAttributeNow"
                />
              </div>
              <div class="col-sm-12 col-lg-4">
                <h4 class="m-0">
                  {{ $t('layoutImageEditComp.layoutImageElements') }}
                </h4>
                <hr class="m-0 mb-3">

                <div
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('name') }}</label>
                  <div class="col-12 col-md-10">
                    <input
                      v-model="layoutImageElement.name"
                      v-focus
                      :class="['form-control m-input m-input--air', { 'is-invalid': this.$validator.errors.has('Name') }]"
                      type="text"
                    >
                    <span
                      v-show="errors.has('Name')"
                      class="badge badge-danger"
                    >{{ errors.first('Name') }}</span>
                  </div>
                </div>

                <div
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('themeElementEdit.doScale') }}</label>
                  <div class="col-12 col-md-10">
                    <toggle-button
                      v-model="layoutImageElement.doScale"
                      :width="50"
                      :height="25"
                      class="m-0"
                    />
                  </div>
                </div>

                <template v-if="layoutImageElement.doScale">
                  <div
                    class="form-group row mb-4"
                  >
                    <label class="col-12 col-md-2">{{ $t('themeElementEdit.anchor') }}</label>
                    <div class="col-12 col-md-10">
                      <select
                        v-model="layoutImageElement.anchor"
                        :class="['form-control', { 'is-invalid': this.$validator.errors.has('Anchor') }]"
                      >
                        <option
                          v-for="(key, index) in anchors"
                          :key="`cutConfigListItemAdd_Option${ index }`"
                          :value="key"
                        >
                          {{ key }}
                        </option>
                      </select>
                      <span
                        v-show="errors.has('Anchor')"
                        class="badge badge-danger"
                      >{{ errors.first('Anchor') }}</span>
                    </div>
                  </div>

                  <div
                    class="form-group row mb-4"
                  >
                    <label class="col-12 col-md-2">{{ $t('themeElementEdit.scaleMode') }}</label>
                    <div class="col-12 col-md-10">
                      <select
                        v-model="layoutImageElement.scaleMode"
                        :class="['form-control', { 'is-invalid': this.$validator.errors.has('ScaleMode') }]"
                      >
                        <option
                          v-for="(key, index) in scaleModes"
                          :key="`cutConfigListItemAdd_Option${ index }`"
                          :value="key"
                        >
                          {{ key }}
                        </option>
                      </select>
                      <span
                        v-show="errors.has('ScaleMode')"
                        class="badge badge-danger"
                      >{{ errors.first('ScaleMode') }}</span>
                    </div>
                  </div>
                </template>

                <div
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('themeElementEdit.collection') }}</label>
                  <div class="col-12 col-md-10">
                    <toggle-button
                      v-model="layoutImageElement.isImageCollection"
                      :width="50"
                      :height="25"
                      class="m-0"
                    />
                  </div>
                </div>

                <template v-if="layoutImageElement.isImageCollection">
                  <div
                    class="form-group row mb-4"
                  >
                    <label class="col-12 col-md-2">{{ $t('themeElementEdit.imageCollection') }}</label>
                    <div class="col-12 col-md-10">
                      <select
                        v-model="layoutImageElement.imageCollection"
                        :class="['form-control', { 'is-invalid': this.$validator.errors.has('ImageCollection') }]"
                      >
                        <option
                          :value="null"
                          disabled
                        >
                          {{ $t('pleaseSelectOne') }}
                        </option>
                        <option
                          v-for="(collection, index) in imageCollections"
                          :key="`imageColection${ index }`"
                          :value="collection.name"
                        >
                          {{ collection.name }}
                        </option>
                      </select>
                      <span
                        v-show="errors.has('ImageCollection')"
                        class="badge badge-danger"
                      >{{ errors.first('ImageCollection') }}</span>
                    </div>
                  </div>
                </template>

                <div
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">Sort Number</label>
                  <div class="col-12 col-md-10">
                    <input
                      v-model="layoutImageElement.renderSortNumber"
                      :class="['form-control m-input m-input--air', { 'is-invalid': this.$validator.errors.has('RenderSortNumber') }]"
                      type="number"
                    >
                    <span
                      v-show="errors.has('RenderSortNumber')"
                      class="badge badge-danger"
                    >{{ errors.first('RenderSortNumber') }}</span>
                  </div>
                </div>

                <template>
                  <h4 class="m-0 mt-4">
                    {{ $t('start') }}
                  </h4>
                  <hr class="m-0 mb-3">
                </template>
                
                <div
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('themeElementEdit.eventKey') }}</label>
                  <div class="col-12 col-md-10">
                    <select
                      v-model="layoutImageElement.startEventKey"
                      :class="['form-control', { 'is-invalid': this.$validator.errors.has('StartEventKey') }]"
                    >
                      <option
                        v-for="(key, index) in startModifiedEventKeys"
                        :key="`cutConfigListItemAdd_Option${ index }`"
                        :value="key"
                      >
                        {{ key }}
                      </option>
                    </select>
                    <span
                      v-show="errors.has('StartEventKey')"
                      class="badge badge-danger"
                    >{{ errors.first('StartEventKey') }}</span>
                  </div>
                </div>

                <div
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('themeElementEdit.eventReporter') }}</label>
                  <div class="col-12 col-md-10">
                    <select
                      v-model="layoutImageElement.startEventReporter"
                      :class="['form-control', { 'is-invalid': this.$validator.errors.has('StartEventReporter') }]"
                      @change="setBackStartEventReporterIndex(); setStartEventKeyListValues($event)"
                    >
                      <option
                        v-for="(key, index) in eventReporters"
                        :key="`cutConfigListItemAdd_Option2${ index }`"
                        :value="key"
                      >
                        {{ key }}
                      </option>
                    </select>
                    <span
                      v-show="errors.has('StartEventReporter')"
                      class="badge badge-danger"
                    >{{ errors.first('StartEventReporter') }}</span>
                  </div>
                </div>
                <div
                  v-if="layoutImageElement.startEventReporter == 'VideoRecorder'"
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('themeElementEdit.eventReporterIndex') }} </label>
                  <div class="col-12 col-md-10">
                    <input
                      v-model="layoutImageElement.startEventReporterIndex"
                      :class="['form-control m-input m-input--air', { 'is-invalid': this.$validator.errors.has('StartEventIndex') }]"
                      type="number"
                    >
                    <span
                      v-show="errors.has('StartEventIndex')"
                      class="badge badge-danger"
                    >{{ errors.first('StartEventIndex') }}</span>
                  </div>
                </div>

                <div
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('themeElementEdit.offset') }} <small>({{ $t('seconds') }})</small></label>
                  <div class="col-12 col-md-10">
                    <input
                      v-model="layoutImageElement.startOffset"
                      :class="['form-control m-input m-input--air', { 'is-invalid': this.$validator.errors.has('StartOffset') }]"
                      type="number"
                    >
                    <span
                      v-show="errors.has('StartOffset')"
                      class="badge badge-danger"
                    >{{ errors.first('StartOffset') }}</span>
                  </div>
                </div>

                <template>
                  <h4 class="m-0 mt-4">
                    {{ $t('end') }}
                  </h4>
                  <hr class="m-0 mb-3">
                </template>

                <div
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('themeElementEdit.eventKey') }}</label>
                  <div class="col-12 col-md-10">
                    <select
                      v-model="layoutImageElement.endEventKey"
                      :class="['form-control', { 'is-invalid': this.$validator.errors.has('EndEventKey') }]"
                    >
                      <option
                        v-for="(key, index) in endModifiedEventKeys"
                        :key="`cutConfigListItemAdd_Option2${ index }`"
                        :value="key"
                      >
                        {{ key }}
                      </option>
                    </select>
                    <span
                      v-show="errors.has('EndEventKey')"
                      class="badge badge-danger"
                    >{{ errors.first('EndEventKey') }}</span>
                  </div>
                </div>

                <div
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('themeElementEdit.eventReporter') }}</label>
                  <div class="col-12 col-md-10">
                    <select
                      v-model="layoutImageElement.endEventReporter"
                      :class="['form-control', { 'is-invalid': this.$validator.errors.has('EndEventReporter') }]"
                      @change="setBackEndEventReporterIndex(); setEndEventKeyListValues($event)"
                    >
                      <option
                        v-for="(key, index) in eventReporters"
                        :key="`cutConfigListItemAdd_Option2${ index }`"
                        :value="key"
                      >
                        {{ key }}
                      </option>
                    </select>
                    <span
                      v-show="errors.has('EndEventReporter')"
                      class="badge badge-danger"
                    >{{ errors.first('EndEventReporter') }}</span>
                  </div>
                </div>
                <div
                  v-if="layoutImageElement.endEventReporter == 'VideoRecorder'"
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('themeElementEdit.eventReporterIndex') }} </label>
                  <div class="col-12 col-md-10">
                    <input
                      v-model="layoutImageElement.endEventReporterIndex"
                      :class="['form-control m-input m-input--air', { 'is-invalid': this.$validator.errors.has('EndEventIndex') }]"
                      type="number"
                    >
                    <span
                      v-show="errors.has('EndEventIndex')"
                      class="badge badge-danger"
                    >{{ errors.first('EndEventIndex') }}</span>
                  </div>
                </div>

                <div
                  class="form-group row mb-4"
                >
                  <label class="col-12 col-md-2">{{ $t('offset') }} <small>({{ $t('seconds') }})</small></label>
                  <div class="col-12 col-md-10">
                    <input
                      v-model="layoutImageElement.endOffset"
                      :class="['form-control m-input m-input--air', { 'is-invalid': this.$validator.errors.has('EndOffset') }]"
                      type="number"
                    >
                    <span
                      v-show="errors.has('EndOffset')"
                      class="badge badge-danger"
                    >{{ errors.first('EndOffset') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ThemeImageUploader
            v-if="(layoutImageId || userDependentImageId) && !layoutImageElement.isImageCollection"
            :layout-image-id="layoutImageId"
            :user-dependent-image-id="userDependentImageId"
            :file-is-available="layoutImageElement.fileIsAvailable"
            @imageUrlUpdated="setImageUrl"
          />
        </div>
        <button
          class="btn btn-sm btn-secondary float-left"
          @click="cancel()"
        >
          <font-awesome-icon
            class="mr-2"
            icon="times"
          />{{ $t('cancel') }}
        </button>
        <button
          class="btn btn-sm btn-primary float-right"
          @click="update()"
        >
          <font-awesome-icon
            class="mr-2"
            icon="check"
          />{{ $t('save') }}
        </button>
        <div class="clearfix" />
      </template>
      <template v-else>
        {{ $t('noDataAvailable') }}
      </template>
    </template>
  </Portlet>
</template>

<script>
import { errorMixin } from '@/mixins/errorMixin.js';

export default {
  name: "LayoutImageEdit",
  components: {
    Preview: () => import('@/components/VideoPostprocessing/Preview.vue'),
    ThemeImageUploader: () => import('@/components/VideoPostprocessing/Theme/ThemeImageUploader.vue')
  },
  mixins: [
    errorMixin
  ],
  props: {
    themeId: {
      type: String,
      required: true
    },
    elementId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      loading: true,
      layoutImageElement: null,
      eventKeys: null,
      eventReporters: null,
      anchors: null,
      scaleModes: null,
      imageUrl: null,
      imageCollections: null,
      previewOtherElements: true,
      collectionType: 'Images',
      startFinishEventKeys: [
        'SessionStarted', 
        'SessionFinished', 
        'RaceStarted', 
        'RaceFinished', 
        'RaceAborted'
      ],
      startModifiedEventKeys: null,
      endModifiedEventKeys: null
    }
  },
  computed: {
    portletTilte () {
      if (this.theme == null) {
        return this.$t('themeElementEdit.themeElementEdit');
      }
      if (this.theme.name == null) {
        return this.$t('themeElementEdit.themeElementEdit');
      }
      return `${ this.$t('themeElementEdit.themeElementEdit') }: ${ this.theme.name }`;
    },
    userDependentImageId (){
      if (this.$route.fullPath.includes("userdependentimage")) {
          return this.elementId;
      }
      return null;
    },
    layoutImageId (){
      if (this.$route.fullPath.includes("layoutimage")) {
        return this.elementId;
      }
      return null;
    }
  },
  created () {
    this.getDataWithLoading();
  },
  methods: {
    setImageUrl (url){
      this.imageUrl = url;
    },
    cancel () {
      this.$router.push(`/videopostprocessing/theme/${ this.themeId }/layoutimageelement`);
    },
    getData () {
      this.axios.all([
        this.axios.get(`/VideoPostProcessing/GetLayoutImageElement?layoutImageElementId=${ this.elementId }`),
        this.axios.get(`/VideoPostprocessing/GetEventKeys`),
        this.axios.get(`/VideoPostprocessing/GetEventReporters`),
        this.axios.get(`/VideoPostprocessing/GetImageAnchors`),
        this.axios.get(`/VideoPostprocessing/GetImageScaleModes`),
        this.axios.get(`/VideoPostprocessing/GetAllImageCollections?collectionType=${ this.collectionType }`)
      ])
        .then(this.axios.spread((imageElementResponse, keyResponse, reportResponse, anchorResponse, scaleModeResponse, imageCollectionResponse) => {
          if (imageElementResponse && imageElementResponse.data)  {
            this.layoutImageElement = imageElementResponse.data;
          }

          if (keyResponse && keyResponse.data ) {
            this.eventKeys = keyResponse.data;
          }
          if (reportResponse && reportResponse.data) {
            this.eventReporters = reportResponse.data;
          }
          if (anchorResponse && anchorResponse.data) {
            this.anchors = anchorResponse.data;
          }          
          if (scaleModeResponse && scaleModeResponse.data) {
            this.scaleModes = scaleModeResponse.data;
          }
          if (imageCollectionResponse && imageCollectionResponse.data) {
            this.imageCollections = imageCollectionResponse.data;
          }
          
          var startEventReporterVal = this.layoutImageElement.startEventReporter;
          var endEventReporterVal = this.layoutImageElement.endEventReporter;

          if (startEventReporterVal === "StartFinish") {
            this.startModifiedEventKeys = this.eventKeys.filter(x => this.startFinishEventKeys.includes(x) && x != "Unknown");
          } else if (startEventReporterVal === "VideoRecorder") {
            this.startModifiedEventKeys = this.eventKeys.filter(x => !this.startFinishEventKeys.includes(x) && x != "Unknown");
          } else {
            this.startModifiedEventKeys = this.eventKeys.filter(x => x == "Unknown");
            this.startEventKey = "Unknown";
          }

          if (!this.startModifiedEventKeys.includes(this.layoutImageElement.startEventKey)) {
            this.layoutImageElement.startEventKey = this.startModifiedEventKeys.find(x => x);
          }

          if (endEventReporterVal === "StartFinish") {
            this.endModifiedEventKeys = this.eventKeys.filter(x => this.startFinishEventKeys.includes(x) && x != "Unknown");
          } else if (endEventReporterVal === "VideoRecorder") {
            this.endModifiedEventKeys = this.eventKeys.filter(x => !this.startFinishEventKeys.includes(x) && x != "Unknown");
          } else {
            this.endModifiedEventKeys = this.eventKeys.filter(x => x == "Unknown");
            this.endEventKey = "Unknown";
          }

          if (!this.endModifiedEventKeys.includes(this.layoutImageElement.endEventKey)) {
            this.layoutImageElement.endEventKey = this.endModifiedEventKeys.find(x => x);
          }

        }))
        .finally(() => {
          this.loading = false;
        });
    },
    getDataWithLoading () {
      this.loading = true;
      this.getData();
    },
    update () {
      this.axios.put(`/VideoPostProcessing/UpdateLayoutImageElement`, this.layoutImageElement)
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.status == null) {
            return;
          }
          if (response.status != 200) {
            return;
          }
          this.error_clear();
          this.$snotify.success(this.$t('layoutImageEditComp.updateSuccess'));
        })
        .catch((error) => {
          this.error_clear();
          this.error_validate(error);
          this.loading = false;
        });
    },
    deleteElement () {
      this.axios.delete(`/VideoPostProcessing/DeleteLayoutImageElement?layoutImageElementId=${ this.elementId }`)
        .then(() => {
          this.$snotify.success(this.$t('themeElementEdit.themeDeleteSuccess'));
          this.$router.push({path: `/videopostprocessing/theme/${ this.themeId }` });
        })
        .catch(() => {
          this.loading = false;
        });
    },
    changeAttributeNow (value, attribute) {
      this.layoutImageElement[attribute] = value;
    },
    setBackStartEventReporterIndex () {
      if(this.layoutImageElement.startEventReporter != 'VideoRecorder')
      {
        this.layoutImageElement.startEventReporterIndex = '0';
      }
      else if(this.layoutImageElement.startEventReporterIndex === undefined)
      {
        this.layoutImageElement.startEventReporterIndex = '1';
      }
    },
    setBackEndEventReporterIndex () {
      if(this.layoutImageElement.endEventReporter != 'VideoRecorder')
      {
        this.layoutImageElement.endEventReporterIndex = '0';
      }
      else if(this.layoutImageElement.endEventReporterIndex === undefined)
      {
        this.layoutImageElement.endEventReporterIndex = '1';
      }
    },
    setStartEventKeyListValues (event) {
      var val = event.target.value;

      if (val === "StartFinish") {
          this.startModifiedEventKeys = this.eventKeys.filter(x => this.startFinishEventKeys.includes(x) && x != "Unknown");
      } else if (val === "VideoRecorder") {
          this.startModifiedEventKeys = this.eventKeys.filter(x => !this.startFinishEventKeys.includes(x) && x != "Unknown");
      } else {
          this.startModifiedEventKeys = this.eventKeys.filter(x => x == "Unknown");
      }

      this.layoutImageElement.startEventKey = this.startModifiedEventKeys.find(x => x);
    },
    setEndEventKeyListValues (event) {
      var val = event.target.value;

      if (val === "StartFinish") {
          this.endModifiedEventKeys = this.eventKeys.filter(x => this.startFinishEventKeys.includes(x) && x != "Unknown");
      } else if (val === "VideoRecorder") {
          this.endModifiedEventKeys = this.eventKeys.filter(x => !this.startFinishEventKeys.includes(x) && x != "Unknown");
      } else {
          this.endModifiedEventKeys = this.eventKeys.filter(x => x == "Unknown");
      }

      this.layoutImageElement.endEventKey = this.endModifiedEventKeys.find(x => x);
    }
  }
}
</script>
